import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Option } from 'types';
import { CatalogueMenu } from 'types/api/product.type';

import { productDataService } from 'services/products.service';

import { useCatalogueLabels } from 'hooks';

import { registerCatalogueLabels } from 'helpers/registerCatalogueLabels';

import { Button } from 'components/Button';
import { Modal } from 'components/Modal';
import { SelectWithControl } from 'components/SelectWithControl';

import * as S from './TemplateModal.styles';
import { groupCategoriesByLabel } from './groupCategoriesByLabel';

type TemplateModalProps = {
  onClose: () => void;
  onDownload: (params: { gender?: string; category?: string }) => void;
  isLoading: boolean;
  season: string;
  collectionCode: string;
};

const ALL_GENDER = 'ALL_GENDER';
const ALL_CATEGORY = 'ALL_CATEGORY';

const TemplateModal = ({
  onClose,
  onDownload,
  isLoading,
  season,
  collectionCode,
}: TemplateModalProps) => {
  const { t } = useTranslation();
  const { genreLabel, categoryLabel } = useCatalogueLabels();
  const { control, setValue } = useForm();
  const [gender, setGender] = useState(ALL_GENDER);
  const [category, setCategory] = useState(ALL_CATEGORY);
  const [genres, setGenres] = useState<CatalogueMenu[]>([]);
  const [isFetchingGenres, setIsFetchingGenres] = useState(false);

  const fetchGenres = useCallback(async () => {
    if (!season || !collectionCode) return;
    setIsFetchingGenres(true);
    try {
      const res = await productDataService.getMenu(season, collectionCode);
      registerCatalogueLabels(res.data);
      setGenres(res.data);
    } catch {
      setGenres([]);
    } finally {
      setIsFetchingGenres(false);
    }
  }, [season, collectionCode]);

  useEffect(() => {
    fetchGenres();
  }, [fetchGenres]);

  const genderOptions: Option[] = useMemo(() => {
    const sorted = [...genres].sort((a, b) => a.nodeNameOrder - b.nodeNameOrder);
    return [
      { value: ALL_GENDER, label: t('catalogue.entireCollection') },
      ...sorted.map((g) => ({
        value: g.nodeName,
        label: genreLabel(g.nodeName),
      })),
    ];
  }, [genres, t, genreLabel]);

  const categoryOptions: Option[] = useMemo(() => {
    if (gender === ALL_GENDER) {
      return [
        { value: ALL_CATEGORY, label: t('catalogue.allCategories') },
        ...groupCategoriesByLabel(genres, categoryLabel),
      ];
    }

    const selectedGenre = genres.find((g) => g.nodeName === gender);
    if (!selectedGenre) {
      return [{ value: ALL_CATEGORY, label: t('catalogue.allCategories') }];
    }

    const sorted = [...selectedGenre.nodeName2].sort((a, b) => a.order - b.order);
    return [
      { value: ALL_CATEGORY, label: t('catalogue.allCategories') },
      ...sorted.map((cat) => ({ value: cat.name, label: categoryLabel(cat.name) })),
    ];
  }, [gender, genres, t, categoryLabel]);

  useEffect(() => {
    if (category !== ALL_CATEGORY && !categoryOptions.some((opt) => opt.value === category)) {
      setCategory(ALL_CATEGORY);
      setValue('category', ALL_CATEGORY);
    }
  }, [category, categoryOptions, setValue]);

  const handleGenderSelect = (opt: Option) => {
    setGender(String(opt.value));
  };

  const handleCategorySelect = (opt: Option) => {
    setCategory(String(opt.value));
  };

  const handleDownload = () => {
    const params: { gender?: string; category?: string } = {};
    if (gender && gender !== ALL_GENDER) params.gender = gender;
    if (category && category !== ALL_CATEGORY) params.category = category;
    onDownload(params);
  };

  return (
    <Modal onClose={onClose}>
      <S.Container>
        <S.Title scale="xl3" color="grayDark">
          {t('orderTable.downloadTemplate')}
        </S.Title>

        <S.Content>
          <S.Description fontFamily="LFT Zeno" fontWeight="extraLight" scale="s">
            {t('catalogue.selectFilters')}
          </S.Description>

          <S.FieldWrapper>
            <SelectWithControl
              name="gender"
              options={genderOptions}
              control={control}
              label={t('catalogue.gender')}
              disabled={isFetchingGenres}
              motive="filter"
              onSelect={handleGenderSelect}
              isSearchable={false}
              hideEmptyOption
              defaultValue={ALL_GENDER}
            />
          </S.FieldWrapper>

          <S.FieldWrapper>
            <SelectWithControl
              name="category"
              options={categoryOptions}
              control={control}
              label={t('catalogue.category')}
              disabled={isFetchingGenres}
              motive="filter"
              onSelect={handleCategorySelect}
              isSearchable={false}
              hideEmptyOption
              defaultValue={ALL_CATEGORY}
            />
          </S.FieldWrapper>
        </S.Content>

        <S.ButtonWrapper>
          <Button
            onClick={onClose}
            motive="cancelTertiary"
            fontFamily="LFT Zeno"
            textTransform="isFirstLetterUppercase"
          >
            {t('common.cancel')}
          </Button>

          <Button
            textTransform="isFirstLetterUppercase"
            fontWeight="light"
            fontFamily="LFT Zeno"
            motive="actionTertiary"
            onClick={handleDownload}
            isLoading={isLoading}
          >
            {t('common.download')}
          </Button>
        </S.ButtonWrapper>
      </S.Container>
    </Modal>
  );
};

export { TemplateModal };
